<template>
  <div class="dashboard dashboard-lab">
    <b-container fluid class="dashboard-lab-container">
      <section class="welcome-panel">
        <div class="welcome-copy">
          <h2>{{ labels.welcomeTitle }}</h2>
        </div>
      </section>

      <section class="outside-toolbar">
        <button
          v-if="canManageTags"
          class="btn btn-info btn-sm"
          @click="handleCreateClick"
        >
          {{ labels.createTag }}
        </button>
        <small v-else class="readonly-badge">{{ labels.readOnly }}</small>
        <small>{{ labels.tagCounter(tags.length, maxTags) }}</small>
      </section>

      <section v-if="showCreator" class="creator-panel">
        <div class="creator-card">
          <h5>{{ labels.creatorTitle }}</h5>
          <div class="creator-grid">
            <div>
              <label class="form-label">{{ labels.tagTitle }}</label>
              <input
                v-model="draft.title"
                class="form-control form-control-sm"
                :placeholder="labels.tagTitlePlaceholder"
              >
            </div>
            <div>
              <label class="form-label">{{ labels.infoType }}</label>
              <select v-model="draft.infoKey" class="form-select form-select-sm">
                <option v-for="opt in infoOptions" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
            </div>
            <div class="creator-field-full">
              <label class="form-label">{{ labels.presentationType }}</label>
              <select v-model="draft.presentationType" class="form-select form-select-sm">
                <option v-for="opt in creatorPresentationOptions" :key="`creator_pres_${opt.value}`" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
              <div class="presentation-preview">
                <span class="presentation-thumb" :class="presentationThumbClass(draft.presentationType)"></span>
                <small>{{ presentationLabel(draft) }}</small>
              </div>
            </div>
          </div>
          <div class="creator-actions">
            <button class="btn btn-info btn-sm" :disabled="isLimitReached" @click="createTag">
              {{ labels.saveTag }}
            </button>
            <button class="btn btn-outline-secondary btn-sm" @click="showCreator = false">
              {{ labels.cancel }}
            </button>
          </div>
        </div>
      </section>

      <section v-if="shouldShowPeriodFilter" class="period-filter-panel">
        <div class="period-filter-card">
          <h6>{{ labels.periodFilterTitle }}</h6>
          <div class="period-filter-grid">
            <div>
              <label class="form-label">{{ labels.basePeriod }}</label>
              <select v-model="selectedPeriodoActual" class="form-select form-select-sm">
                <option
                  v-for="periodo in periodosDisponibles"
                  :key="`periodo_actual_${periodo}`"
                  :value="periodo"
                >
                  {{ periodo }}
                </option>
              </select>
            </div>
            <div>
              <label class="form-label">{{ labels.comparePeriod }}</label>
              <select v-model="selectedPeriodoComparado" class="form-select form-select-sm">
                <option
                  v-for="periodo in periodosDisponibles"
                  :key="`periodo_comparado_${periodo}`"
                  :value="periodo"
                >
                  {{ periodo }}
                </option>
              </select>
            </div>
            <div class="period-filter-actions">
              <button class="btn btn-info btn-sm" :disabled="isLoadingMetricas" @click="applyPeriodoFilter">
                {{ labels.applyPeriods }}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section ref="canvas" class="tags-canvas">
        <div v-if="!tags.length" class="canvas-empty">
          <p>{{ labels.emptyState }}</p>
        </div>

        <article
          v-for="tag in tags"
          :key="tag.id"
          class="mini-tag"
          :class="{ active: activeTagId === tag.id, readonly: !canManageTags, editing: editingTagId === tag.id }"
          :style="tagStyle(tag)"
          @pointerdown="startDrag($event, tag.id)"
        >
          <header class="mini-tag-header">
            <strong>{{ tag.title }}</strong>
            <div class="mini-tag-actions">
              <button
                v-if="isComparativoTercero(tag)"
                class="mini-search"
                :class="{ active: searchTagId === tag.id }"
                @pointerdown.stop
                @click="toggleTagSearch(tag.id)"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="11" cy="11" r="6"></circle>
                  <line x1="16" y1="16" x2="21" y2="21"></line>
                </svg>
              </button>
              <button
                v-if="canManageTags"
                class="mini-edit"
                @pointerdown.stop
                @click="openTagEditor(tag.id)"
              >
                ⋮
              </button>
              <button
                v-if="canManageTags"
                class="mini-delete"
                @pointerdown.stop
                @click="deleteTag(tag.id)"
              >
                ×
              </button>
            </div>
          </header>

          <div class="mini-tag-body">
            <div
              v-if="editingTagId !== tag.id && searchTagId === tag.id && isComparativoTercero(tag)"
              class="tag-tercero-filters"
              @pointerdown.stop
            >
              <div class="form-check form-switch">
                <input
                  :id="`tagIncluirTodosTerceros_${tag.id}`"
                  v-model="incluirTodosTerceros"
                  class="form-check-input"
                  type="checkbox"
                >
                <label class="form-check-label" :for="`tagIncluirTodosTerceros_${tag.id}`">
                  {{ labels.showAllTerceros }}
                </label>
              </div>
              <div class="tag-tercero-filter-input">
                <input
                  v-model.trim="terceroFilter"
                  class="form-control form-control-sm"
                  :placeholder="labels.searchTerceroPlaceholder"
                  @pointerdown.stop
                >
              </div>
            </div>
            <template v-if="editingTagId === tag.id">
              <div class="inline-editor" @pointerdown.stop>
                <div class="inline-grid">
                  <div class="inline-field">
                    <small class="inline-label">{{ labels.tagTitle }}</small>
                    <input
                      v-model="editDraft.title"
                      class="form-control form-control-sm inline-input"
                      :placeholder="labels.tagTitlePlaceholder"
                    >
                  </div>
                  <div class="inline-field">
                    <small class="inline-label">{{ labels.infoType }}</small>
                    <select v-model="editDraft.infoKey" class="form-select form-select-sm inline-input">
                      <option v-for="opt in infoOptions" :key="`inline_info_${opt.value}`" :value="opt.value">
                        {{ opt.label }}
                      </option>
                    </select>
                  </div>
                  <div class="inline-field">
                    <small class="inline-label">{{ labels.presentationType }}</small>
                    <select v-model="editDraft.presentationType" class="form-select form-select-sm inline-input">
                      <option v-for="opt in editPresentationOptions" :key="`inline_pres_${opt.value}`" :value="opt.value">
                        {{ opt.label }}
                      </option>
                    </select>
                  </div>
                  <div class="presentation-preview inline-preview">
                    <span class="presentation-thumb" :class="presentationThumbClass(editDraft.presentationType)"></span>
                    <small>{{ presentationLabel(editDraft) }}</small>
                  </div>
                </div>
                <div class="inline-color-row">
                  <input v-model="editDraft.color" type="color" class="form-control form-control-color">
                  <input v-model="editDraft.textColor" type="color" class="form-control form-control-color">
                </div>
              </div>
            </template>
            <template v-else-if="resolvePresentation(tag) === 'pie'">
              <div class="chart-panel">
                <div v-if="pieComparisons(tag).length" class="dual-pie-grid">
                  <div v-for="pie in pieComparisons(tag)" :key="`${tag.id}_pie_${pie.period}`" class="dual-pie-card">
                    <div class="mini-pie" :style="{ background: pie.gradient }"></div>
                    <strong class="dual-pie-title">{{ pie.period }}</strong>
                    <small class="dual-pie-total">{{ labels.total }}: {{ pie.total }}</small>
                  </div>
                </div>
                <div v-if="pieLegend(tag).length" class="mini-series-legend">
                  <span v-for="item in pieLegend(tag)" :key="`${tag.id}_pie_legend_${item.key}`" class="series-chip">
                    <i class="series-dot" :style="{ background: item.color }"></i>
                    {{ item.label }} {{ item.percent }}%
                  </span>
                </div>
                <div class="metric-meta">
                  <span>{{ labels.value }}: {{ metricDetails(tag).value }}</span>
                  <span>{{ labels.percentage }}: {{ metricDetails(tag).percentage }}%</span>
                  <span>{{ labels.total }}: {{ metricDetails(tag).total }}</span>
                </div>
              </div>
            </template>

            <template v-else-if="resolvePresentation(tag) === 'donut'">
              <div class="chart-panel">
                <div v-if="pieComparisons(tag).length" class="dual-pie-grid">
                  <div v-for="pie in pieComparisons(tag)" :key="`${tag.id}_donut_${pie.period}`" class="dual-pie-card">
                    <div class="mini-donut" :style="{ background: pie.gradient }">
                      <span>{{ pie.total }}</span>
                    </div>
                    <strong class="dual-pie-title">{{ pie.period }}</strong>
                  </div>
                </div>
                <div v-if="pieLegend(tag).length" class="mini-series-legend">
                  <span v-for="item in pieLegend(tag)" :key="`${tag.id}_donut_legend_${item.key}`" class="series-chip">
                    <i class="series-dot" :style="{ background: item.color }"></i>
                    {{ item.label }} {{ item.percent }}%
                  </span>
                </div>
                <div class="tag-legend">{{ metricSummary(tag) }}</div>
                <div class="metric-meta">
                  <span>{{ labels.value }}: {{ metricDetails(tag).value }}</span>
                  <span>{{ labels.percentage }}: {{ metricDetails(tag).percentage }}%</span>
                  <span>{{ labels.total }}: {{ metricDetails(tag).total }}</span>
                </div>
              </div>
            </template>

            <template v-else-if="resolvePresentation(tag) === 'bars'">
              <div class="chart-panel bars-panel">
                <div class="mini-bars">
                  <div
                    v-for="group in groupedBarItems(tag)"
                    :key="`${tag.id}_bar_group_${group.key}`"
                    class="mini-bar-group"
                    :title="group.tooltip"
                  >
                    <div class="mini-bar-pair">
                      <div
                        v-for="bar in group.bars"
                        :key="`${group.key}_${bar.period}`"
                        class="mini-bar-item"
                      >
                        <div class="mini-bar-top-metrics">
                          <small class="mini-bar-value">{{ bar.value }}</small>
                          <small class="mini-bar-rate">{{ bar.avgRate }}</small>
                        </div>
                        <span
                          class="mini-bar-fill"
                          :style="{ height: `${bar.height}%`, background: bar.color }"
                        />
                      </div>
                    </div>
                    <small class="mini-bar-label">{{ group.shortLabel }}</small>
                  </div>
                </div>
              <div class="tag-legend">{{ labels.value }}: {{ metricDetails(tag).value }} | {{ labels.percentage }}: {{ metricDetails(tag).percentage }}%</div>
              <div class="metric-meta">
                <span>{{ labels.total }}: {{ metricDetails(tag).total }}</span>
                <span>{{ isSpanish ? "Top categoría" : "Top category" }}: {{ rankingItems(tag)[0]?.label || "-" }}</span>
              </div>
            </div>
          </template>

            <template v-else-if="resolvePresentation(tag) === 'bars_horizontal'">
              <div class="chart-panel">
                <div class="mini-hbars">
                  <div v-for="item in horizontalBarItems(tag)" :key="`${tag.id}_hbar_${item.key}`" class="hbar-row">
                    <span class="hbar-label">{{ item.label }}</span>
                    <div class="hbar-track">
                      <span class="hbar-fill" :style="{ width: `${item.width}%`, background: item.color }"></span>
                    </div>
                    <span class="hbar-value">
                      {{ item.value }} ({{ item.percentage }}%)
                      <template v-if="item.avgRate"> | {{ item.avgRate }}</template>
                    </span>
                  </div>
                </div>
                <div class="metric-meta">
                  <span>{{ labels.value }}: {{ metricDetails(tag).value }}</span>
                  <span>{{ labels.total }}: {{ metricDetails(tag).total }}</span>
                </div>
              </div>
            </template>

            <template v-else-if="resolvePresentation(tag) === 'progress'">
              <div class="chart-panel">
                <div class="progress-track">
                  <span class="progress-fill" :style="progressFillStyle(tag)"></span>
                </div>
                <div class="tag-value kpi-compact">{{ progressPercent(tag) }}%</div>
                <div class="tag-legend">{{ metricSummary(tag) }}</div>
                <div class="metric-meta">
                  <span>{{ labels.value }}: {{ metricDetails(tag).value }}</span>
                  <span>{{ labels.total }}: {{ metricDetails(tag).total }}</span>
                </div>
              </div>
            </template>

            <template v-else-if="resolvePresentation(tag) === 'line'">
              <div class="chart-panel">
                <div class="mini-line">
                  <span
                    v-for="(point, idx) in linePoints(tag)"
                    :key="`${tag.id}_line_${idx}`"
                    class="line-point"
                    :title="pointTitle(point)"
                    :style="{ left: `${point.x}%`, top: `${point.y}%` }"
                  />
                  <svg class="line-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <polyline :points="linePolyline(tag)" :style="lineStrokeStyle(tag)" />
                  </svg>
                </div>
                <div class="metric-meta">
                  <span>{{ labels.value }}: {{ metricDetails(tag).value }}</span>
                  <span>{{ labels.percentage }}: {{ metricDetails(tag).percentage }}%</span>
                  <span>{{ labels.total }}: {{ metricDetails(tag).total }}</span>
                </div>
              </div>
            </template>

            <template v-else-if="resolvePresentation(tag) === 'area'">
              <div class="chart-panel">
                <div class="mini-area">
                  <svg class="line-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <polygon :points="areaPolygon(tag)" :style="areaFillStyle(tag)" />
                    <polyline :points="linePolyline(tag)" :style="lineStrokeStyle(tag)" />
                  </svg>
                </div>
                <div class="metric-meta">
                  <span>{{ labels.value }}: {{ metricDetails(tag).value }}</span>
                  <span>{{ labels.percentage }}: {{ metricDetails(tag).percentage }}%</span>
                  <span>{{ labels.total }}: {{ metricDetails(tag).total }}</span>
                </div>
              </div>
            </template>

            <template v-else-if="resolvePresentation(tag) === 'gauge'">
              <div class="chart-panel">
                <div class="mini-gauge" :style="gaugeStyle(tag)">
                  <span>{{ progressPercent(tag) }}%</span>
                </div>
                <div class="tag-legend">{{ metricSummary(tag) }}</div>
                <div class="metric-meta">
                  <span>{{ labels.value }}: {{ metricDetails(tag).value }}</span>
                  <span>{{ labels.total }}: {{ metricDetails(tag).total }}</span>
                </div>
              </div>
            </template>

            <template v-else-if="resolvePresentation(tag) === 'ranking'">
              <div class="chart-panel">
                <div class="mini-ranking">
                  <div
                    v-for="item in rankingItems(tag)"
                    :key="`${tag.id}_rank_${item.key}`"
                    class="rank-row"
                    :class="{ selected: item.selected }"
                  >
                    <span class="rank-name">{{ item.label }}</span>
                    <strong class="rank-value">
                      {{ item.value }} · {{ item.percentage }}%
                      <template v-if="item.avgRate"> · {{ item.avgRate }}</template>
                    </strong>
                  </div>
                </div>
                <div class="metric-meta">
                  <span>{{ labels.value }}: {{ metricDetails(tag).value }}</span>
                  <span>{{ labels.total }}: {{ metricDetails(tag).total }}</span>
                </div>
              </div>
            </template>

            <template v-else-if="resolvePresentation(tag) === 'table'">
              <div class="chart-panel">
                <div class="mini-table">
                  <div class="table-head">
                    <span>{{ tableGroupTitle(tag) }}</span>
                    <span>{{ isSpanish ? "Fecha" : "Period" }}</span>
                    <span>{{ isSpanish ? "Tasa prom." : "Avg rate" }}</span>
                    <span>{{ isSpanish ? "Registros" : "Records" }}</span>
                  </div>
                  <div v-for="item in tableItems(tag)" :key="`${tag.id}_table_${item.key}`" class="table-row real-row">
                    <span>{{ item.dimensionValue }}</span>
                    <strong>{{ item.fechaCierre }}</strong>
                    <strong>{{ item.tasaPromedio }}%</strong>
                    <strong>{{ item.totalRegistros }}</strong>
                  </div>
                </div>
                <div class="metric-meta">
                  <span>{{ isSpanish ? "Filas" : "Rows" }}: {{ tableItems(tag).length }}</span>
                  <span>{{ labels.total }}: {{ metricDetails(tag).total }}</span>
                </div>
              </div>
            </template>

            <template v-else>
              <div class="tag-value">{{ metricCount(tag) }}</div>
              <div class="tag-legend">{{ metricSummary(tag) }}</div>
              <div class="metric-meta">
                <span>{{ labels.value }}: {{ metricDetails(tag).value }}</span>
                <span>{{ labels.total }}: {{ metricDetails(tag).total }}</span>
                <span>{{ labels.percentage }}: {{ metricDetails(tag).percentage }}%</span>
              </div>
            </template>

            <small v-if="editingTagId !== tag.id">{{ labels.source }}: {{ metricLabel(tag) }} | {{ presentationLabel(tag) }}</small>
          </div>

          <div v-if="editingTagId === tag.id" class="floating-editor-actions" @pointerdown.stop>
            <button
              class="btn btn-info btn-sm"
              :disabled="!hasPendingEditChanges(tag.id)"
              @pointerdown.stop
              @click="saveTagEdit"
            >
              {{ labels.saveChanges }}
            </button>
            <button class="btn btn-outline-secondary btn-sm" @pointerdown.stop @click="cancelTagEdit">
              {{ labels.cancel }}
            </button>
          </div>

          <span
            v-if="canManageTags"
            class="resize-handle"
            @pointerdown.stop="startResize($event, tag.id)"
          />
        </article>
      </section>
    </b-container>
  </div>
</template>

<script>
import Menu from "@/services/Menu";
import DashboardEtiquetas from "@/services/DashboardEtiquetas";
import DashboardMetricas from "@/services/DashboardMetricas";

export default {
  name: "List",
  data: () => ({
    maxTags: 50,
    showCreator: false,
    editingTagId: null,
    canManageTags: false,
    tags: [],
    comparativoTasasRows: [],
    comparativoTasasTipoContratoRows: [],
    comparativoTasasTerceroRows: [],
    comparativoFechas: [],
    searchTagId: null,
    periodosDisponibles: [],
    selectedPeriodoActual: "",
    selectedPeriodoComparado: "",
    incluirTodosTerceros: false,
    terceroFilter: "",
    terceroFilterDebounce: null,
    isLoadingMetricas: false,
    activeTagId: null,
    zSeed: 1,
    interaction: null,
    draft: {
      title: "",
      infoKey: "comparativo_tasas_anual_tipo_activo",
      presentationType: "table",
      color: "#f5faff",
      textColor: "#000000",
    },
    editDraft: {
      title: "",
      infoKey: "comparativo_tasas_anual_tipo_activo",
      presentationType: "table",
      color: "#f5faff",
      textColor: "#000000",
    },
    editOriginal: null,
  }),
  computed: {
    isSpanish() {
      return this.$store.state._language == "es-ES";
    },
    isLimitReached() {
      return this.tags.length >= this.maxTags;
    },
    isEditingAnyTag() {
      return this.editingTagId !== null;
    },
    labels() {
      if (this.isSpanish) {
        return {
          createTag: "Crear etiqueta",
          creatorTitle: "Nueva etiqueta",
          tagTitle: "Título",
          tagTitlePlaceholder: "Ejemplo: Estado reportes activos",
          infoType: "Qué información quieres ver",
          presentationType: "Cómo presentar la etiqueta",
          background: "Color fondo",
          textColor: "Color texto",
          editTitle: "Editar etiqueta",
          saveChanges: "Guardar cambios",
          saveTag: "Guardar etiqueta",
          cancel: "Cancelar",
          emptyState: "No hay etiquetas todavía. Crea la primera para empezar.",
          source: "Fuente",
          totalReports: "Total reportes",
          total: "Total",
          value: "Valor",
          percentage: "Porcentaje",
          otherReports: "Resto",
          tagCounter: (count, limit) => `Etiquetas: ${count}/${limit}`,
          fallbackTagTitle: "Nueva etiqueta",
          noSpace: "No hay espacio disponible para otra etiqueta sin superponer.",
          limitReached: "No se pueden crear mas etiquetas.",
          editCollision: "No se puede editar: la etiqueta se superpone con otra.",
          readOnly: "Solo lectura",
          noPermission: "No tienes permiso para modificar etiquetas.",
          confirmDeleteTag: "¿Deseas eliminar esta etiqueta?",
          noValue: "Sin dato",
          periodFilterTitle: "Períodos para comparativo",
          basePeriod: "Período base",
          comparePeriod: "Período comparado",
          applyPeriods: "Aplicar",
          showAllTerceros: "Ver todos los terceros",
          searchTercero: "Buscar tercero",
          searchTerceroPlaceholder: "Nombre o identificación",
        };
      }
      return {
        welcomeTitle: "Welcome to Leasing",
        createTag: "Create tag",
        creatorTitle: "New tag",
        tagTitle: "Title",
        tagTitlePlaceholder: "Example: Active reports status",
        infoType: "What data do you want",
        presentationType: "How to present the tag",
        background: "Background color",
        textColor: "Text color",
        editTitle: "Edit tag",
        saveChanges: "Save changes",
        saveTag: "Save tag",
        cancel: "Cancel",
        emptyState: "No tags yet. Create the first one to start.",
        source: "Source",
        totalReports: "Total reports",
        total: "Total",
        value: "Value",
        percentage: "Percentage",
        otherReports: "Others",
        tagCounter: (count, limit) => `Tags: ${count}/${limit}`,
        fallbackTagTitle: "New tag",
        noSpace: "There is no free space for another tag without overlap.",
        limitReached: "No more tags can be created.",
        editCollision: "Cannot edit: the tag overlaps another one.",
        readOnly: "Read only",
        noPermission: "You do not have permission to modify tags.",
        confirmDeleteTag: "Do you want to delete this tag?",
        noValue: "No data",
        periodFilterTitle: "Comparison periods",
        basePeriod: "Base period",
        comparePeriod: "Compared period",
        applyPeriods: "Apply",
        showAllTerceros: "Show all third parties",
        searchTercero: "Search third party",
        searchTerceroPlaceholder: "Name or ID",
      };
    },
    infoOptions() {
      if (this.isSpanish) {
        return [
          { value: "comparativo_tasas_anual_tipo_activo", label: "Comparativo de tasas anual por tipo de activo" },
          { value: "comparativo_tasas_anual_tipo_contrato", label: "Comparativo de tasas anual por tipo de contrato" },
          { value: "comparativo_tasas_anual_tercero", label: "Comparativo de tasas anual por tercero" },
        ];
      }
      return [
        { value: "comparativo_tasas_anual_tipo_activo", label: "Annual rate comparison by asset type" },
        { value: "comparativo_tasas_anual_tipo_contrato", label: "Annual rate comparison by contract type" },
        { value: "comparativo_tasas_anual_tercero", label: "Annual rate comparison by third party" },
      ];
    },
    presentationOptions() {
      if (this.isSpanish) {
        return [
          { value: "value", label: "Valor numérico" },
          { value: "bars", label: "Gráfico de barras" },
          { value: "bars_horizontal", label: "Barras horizontales" },
          { value: "line", label: "Gráfico de línea" },
          { value: "area", label: "Gráfico de área" },
          { value: "pie", label: "Gráfico de pastel" },
          { value: "donut", label: "Gráfico de dona" },
          { value: "gauge", label: "Medidor" },
          { value: "progress", label: "Indicador de progreso" },
          { value: "ranking", label: "Ranking" },
          { value: "table", label: "Tabla comparativa" },
        ];
      }
      return [
        { value: "value", label: "Numeric value" },
        { value: "bars", label: "Bar chart" },
        { value: "bars_horizontal", label: "Horizontal bars" },
        { value: "line", label: "Line chart" },
        { value: "area", label: "Area chart" },
        { value: "pie", label: "Pie chart" },
        { value: "donut", label: "Donut chart" },
        { value: "gauge", label: "Gauge" },
        { value: "progress", label: "Progress indicator" },
        { value: "ranking", label: "Ranking" },
        { value: "table", label: "Comparison table" },
      ];
    },
    creatorPresentationOptions() {
      return this.presentationOptionsByInfoKey(this.draft.infoKey);
    },
    editPresentationOptions() {
      return this.presentationOptionsByInfoKey(this.editDraft.infoKey);
    },
    shouldShowPeriodFilter() {
      const comparativos = [
        "comparativo_tasas_anual_tipo_activo",
        "comparativo_tasas_anual_tipo_contrato",
        "comparativo_tasas_anual_tercero",
      ];
      return this.showCreator && comparativos.includes(this.draft.infoKey);
    },
  },
  watch: {
    selectedPeriodoActual(value) {
      if (!value || !this.periodosDisponibles.length) return;
      if (this.selectedPeriodoComparado === value) {
        this.selectedPeriodoComparado = this.getDefaultComparedPeriod(value, this.periodosDisponibles);
      }
    },
    "draft.infoKey"(value) {
      if (value !== "comparativo_tasas_anual_tercero") {
        this.incluirTodosTerceros = false;
        this.terceroFilter = "";
      }
      this.ensureValidPresentationSelection("draft");
    },
    "editDraft.infoKey"() {
      this.ensureValidPresentationSelection("edit");
    },
    incluirTodosTerceros() {
      this.triggerLiveTerceroFilter();
    },
    terceroFilter() {
      this.triggerLiveTerceroFilter();
    },
  },
  methods: {
    buildComparativoGroups(infoKey, limit = 5) {
      const rows = this.getRowsByInfoKey(infoKey);
      const rowPeriods = [...new Set(rows.map((row) => String(row?.fecha_cierre || "")))]
        .filter(Boolean)
        .sort()
        .reverse();
      const periodCandidates = this.comparativoFechas
        .filter((period) => rowPeriods.includes(period));
      const periods = (periodCandidates.length ? periodCandidates : rowPeriods).slice(0, 2);
      const grouped = {};

      rows.forEach((row) => {
        const dimension = this.dimensionValueFromRow(row, infoKey);
        const period = String(row?.fecha_cierre || "");
        if (!grouped[dimension]) grouped[dimension] = {};
        grouped[dimension][period] = {
          registros: Number(row?.total_registros) || 0,
          tasa: Number(row?.tasa) || 0,
        };
      });

      return Object.entries(grouped)
        .map(([label, byPeriod]) => {
          const total = periods.reduce((acc, period) => {
            const periodData = byPeriod[period] || {};
            return acc + (Number(periodData.registros) || 0);
          }, 0);
          return { label, byPeriod, total };
        })
        .sort((a, b) => b.total - a.total)
        .slice(0, limit)
        .map((group, index) => ({
          key: `${group.label}_${index}`,
          label: group.label,
          shortLabel: String(group.label || "").slice(0, 16),
          periods,
          bars: periods.map((period) => {
            const periodData = group.byPeriod[period] || {};
            return {
              period,
              periodShort: period.slice(2, 4),
              value: Number(periodData.registros) || 0,
              avgRate: `${Number(periodData.tasa || 0).toFixed(4)}%`,
            };
          }),
        }));
    },
    presentationOptionsByInfoKey() {
      return this.presentationOptions;
    },
    ensureValidPresentationSelection(target = "draft") {
      const source = target === "edit" ? this.editDraft : this.draft;
      if (!source) return;
      const available = this.presentationOptionsByInfoKey(source.infoKey);
      if (!available.length) return;
      if (!available.some((opt) => opt.value === source.presentationType)) {
        source.presentationType = available[0].value;
      }
    },
    isComparativoTercero(tag) {
      return this.resolveInfoKey(tag) === "comparativo_tasas_anual_tercero";
    },
    async toggleTagSearch(tagId) {
      if (this.searchTagId === tagId) {
        this.searchTagId = null;
        return;
      }
      this.searchTagId = tagId;
      // Restablece vista inicial (Top 10, sin texto) antes de buscar manualmente.
      this.incluirTodosTerceros = false;
      this.terceroFilter = "";
      await this.applyPeriodoFilter();
    },
    triggerLiveTerceroFilter() {
      if (this.searchTagId === null) return;
      if (this.terceroFilterDebounce) {
        clearTimeout(this.terceroFilterDebounce);
      }
      this.terceroFilterDebounce = setTimeout(() => {
        this.applyPeriodoFilter();
      }, 250);
    },
    getTagMinSize(tagLike) {
      const presentation = this.resolvePresentation(tagLike);
      if (presentation === "bars") return { minWidth: 260, minHeight: 175 };
      if (presentation === "bars_horizontal") return { minWidth: 280, minHeight: 190 };
      if (presentation === "line" || presentation === "area") return { minWidth: 280, minHeight: 190 };
      if (presentation === "pie") return { minWidth: 240, minHeight: 175 };
      if (presentation === "donut") return { minWidth: 250, minHeight: 185 };
      if (presentation === "gauge") return { minWidth: 250, minHeight: 175 };
      if (presentation === "progress") return { minWidth: 240, minHeight: 160 };
      if (presentation === "ranking" || presentation === "table") return { minWidth: 420, minHeight: 220 };
      return { minWidth: 190, minHeight: 130 };
    },
    toggleCreator() {
      if (!this.ensureCanManageTags()) return;
      this.showCreator = !this.showCreator;
      if (this.showCreator) this.cancelTagEdit();
    },
    ensureCanManageTags(notify = true) {
      if (this.canManageTags) return true;
      if (notify && this.$toast?.warning) {
        this.$toast.warning(this.labels.noPermission);
      }
      return false;
    },
    notifyLimitReached() {
      if (this.$toast?.warning) {
        this.$toast.warning(this.labels.limitReached);
      }
    },
    handleCreateClick() {
      if (!this.ensureCanManageTags()) return;
      if (this.isLimitReached) {
        this.notifyLimitReached();
        return;
      }
      this.toggleCreator();
    },
    openTagEditor(tagId) {
      if (!this.ensureCanManageTags()) return;
      if (this.editingTagId === tagId) {
        this.cancelTagEdit();
        return;
      }
      const tag = this.tags.find((item) => item.id === tagId);
      if (!tag) return;
      this.editingTagId = tagId;
      this.editDraft = {
        title: tag.title || "",
        infoKey: this.resolveInfoKey(tag),
        presentationType: this.resolvePresentation(tag),
        color: tag.color || "#f5faff",
        textColor: tag.textColor || "#000000",
      };
      this.ensureValidPresentationSelection("edit");
      this.editOriginal = { ...this.editDraft };
      this.showCreator = false;
      this.searchTagId = null;
    },
    cancelTagEdit() {
      this.editingTagId = null;
      this.editOriginal = null;
    },
    hasPendingEditChanges(tagId = null) {
      if (!this.editOriginal || this.editingTagId === null) return false;
      if (tagId !== null && tagId !== this.editingTagId) return false;

      const current = {
        title: (this.editDraft.title || "").trim(),
        infoKey: this.editDraft.infoKey,
        presentationType: this.editDraft.presentationType,
        color: this.editDraft.color,
        textColor: this.editDraft.textColor,
      };
      const original = {
        title: (this.editOriginal.title || "").trim(),
        infoKey: this.editOriginal.infoKey,
        presentationType: this.editOriginal.presentationType,
        color: this.editOriginal.color,
        textColor: this.editOriginal.textColor,
      };

      return (
        current.title !== original.title ||
        current.infoKey !== original.infoKey ||
        current.presentationType !== original.presentationType ||
        current.color !== original.color ||
        current.textColor !== original.textColor
      );
    },
    buildEtiquetaPayload(tag) {
      return {
        id: tag.id,
        idUser: this.$store.state.userId,
        titulo: tag.title,
        info_key: tag.infoKey,
        presentation_type: tag.presentationType,
        pos_x: tag.left,
        pos_y: tag.top,
        width_px: tag.width,
        height_px: tag.height,
        color_fondo: tag.color,
        color_texto: tag.textColor,
        z_index: tag.zIndex,
      };
    },
    async persistTag(tag) {
      if (!tag?.id) return;
      try {
        await DashboardEtiquetas.update(this.buildEtiquetaPayload(tag));
      } catch (error) {
        this.$toast?.error?.("No se pudo guardar la etiqueta en base de datos.");
      }
    },
    async saveTagEdit() {
      if (!this.ensureCanManageTags()) return;
      const tag = this.tags.find((item) => item.id === this.editingTagId);
      if (!tag) {
        this.cancelTagEdit();
        return;
      }
      if (!this.hasPendingEditChanges()) {
        this.cancelTagEdit();
        return;
      }

      const minSize = this.getTagMinSize(this.editDraft);
      const nextWidth = Math.max(tag.width, minSize.minWidth);
      const nextHeight = Math.max(tag.height, minSize.minHeight);
      const candidate = this.toRect(tag, { width: nextWidth, height: nextHeight });
      if (this.hasCollision(candidate, tag.id)) {
        if (this.$toast?.warning) this.$toast.warning(this.labels.editCollision);
        return;
      }

      tag.title = this.editDraft.title?.trim() || this.labels.fallbackTagTitle;
      tag.infoKey = this.editDraft.infoKey;
      tag.presentationType = this.editDraft.presentationType;
      tag.color = this.editDraft.color || "#f5faff";
      tag.textColor = this.editDraft.textColor || "#000000";
      tag.width = nextWidth;
      tag.height = nextHeight;
      await this.persistTag(tag);
      this.cancelTagEdit();
    },
    resolveInfoKey(tag) {
      if (tag.infoKey === "comparativo_tasas_anual_tipo_activo") return tag.infoKey;
      if (tag.infoKey === "comparativo_tasas_anual_tipo_contrato") return tag.infoKey;
      if (tag.infoKey === "comparativo_tasas_anual_tercero") return tag.infoKey;
      return "comparativo_tasas_anual_tipo_activo";
    },
    getRowsByInfoKey(infoKey) {
      if (infoKey === "comparativo_tasas_anual_tercero") return this.comparativoTasasTerceroRows;
      if (infoKey === "comparativo_tasas_anual_tipo_contrato") return this.comparativoTasasTipoContratoRows;
      if (infoKey === "comparativo_tasas_anual_tipo_activo") return this.comparativoTasasRows;
      return [];
    },
    getDimensionKeyByInfoKey(infoKey) {
      if (infoKey === "comparativo_tasas_anual_tercero") return "tercero_nombre";
      if (infoKey === "comparativo_tasas_anual_tipo_contrato") return "tipo_contrato";
      return "tipo_activo";
    },
    tableGroupTitle(tag) {
      const infoKey = this.resolveInfoKey(tag);
      if (infoKey === "comparativo_tasas_anual_tercero") return this.isSpanish ? "Tercero" : "Third party";
      if (infoKey === "comparativo_tasas_anual_tipo_contrato") return this.isSpanish ? "Tipo contrato" : "Contract type";
      return this.isSpanish ? "Tipo activo" : "Asset type";
    },
    dimensionValueFromRow(row, infoKey) {
      const key = this.getDimensionKeyByInfoKey(infoKey);
      let value = row?.[key];
      if ((value === null || value === undefined || value === "") && infoKey === "comparativo_tasas_anual_tercero") {
        value = row?.identificacion;
      }
      if (value === null || value === undefined || value === "") return this.labels.noValue;
      return String(value);
    },
    resolvePresentation(tag) {
      const infoKey = this.resolveInfoKey(tag || {});
      const allowed = this.presentationOptionsByInfoKey(infoKey).map((opt) => opt.value);
      if (allowed.includes(tag?.presentationType)) return tag.presentationType;
      const raw = String(tag?.presentationType || "").toLowerCase();
      const fallbackByRaw = [
        { check: () => raw.includes("horizontal"), value: "bars_horizontal" },
        { check: () => raw.includes("linea") || raw.includes("line"), value: "line" },
        { check: () => raw.includes("area"), value: "area" },
        { check: () => raw.includes("dona") || raw.includes("donut"), value: "donut" },
        { check: () => raw.includes("medidor") || raw.includes("gauge"), value: "gauge" },
        { check: () => raw.includes("progreso") || raw.includes("progress"), value: "progress" },
        { check: () => raw.includes("ranking") || raw.includes("rank"), value: "ranking" },
        { check: () => raw.includes("tabla") || raw.includes("table"), value: "table" },
      ];
      const rawMatch = fallbackByRaw.find((item) => item.check())?.value;
      if (rawMatch && allowed.includes(rawMatch)) return rawMatch;
      return allowed[0] || "table";
    },
    metricCount(tag) {
      const key = this.resolveInfoKey(tag);
      const rows = this.getRowsByInfoKey(key);
      return rows.reduce((acc, row) => acc + (Number(row.total_registros) || 0), 0);
    },
    metricLabel(tag) {
      const key = this.resolveInfoKey(tag);
      const match = this.infoOptions.find((opt) => opt.value === key);
      if (match) return match.label;
      return key;
    },
    presentationLabel(tag) {
      const value = this.resolvePresentation(tag);
      const infoKey = this.resolveInfoKey(tag);
      const scoped = this.presentationOptionsByInfoKey(infoKey);
      const match = scoped.find((opt) => opt.value === value) || this.presentationOptions.find((opt) => opt.value === value);
      return match ? match.label : value;
    },
    presentationThumbClass(value) {
      return `thumb-${String(value || "value").replace(/_/g, "-")}`;
    },
    metricSummary(tag) {
      const rows = this.getRowsByInfoKey(this.resolveInfoKey(tag));
      if (!rows.length) return this.labels.noValue;
      const fechas = this.comparativoFechas.join(" vs ");
      return this.isSpanish ? `Comparativo ${fechas}` : `Comparison ${fechas}`;
    },
    colorSeed(input) {
      const value = String(input || "seed");
      let hash = 0;
      for (let index = 0; index < value.length; index += 1) {
        hash = (hash << 5) - hash + value.charCodeAt(index);
        hash |= 0;
      }
      return Math.abs(hash);
    },
    randomColor(tag, key = "base", alpha = 0.95) {
      const presentation = this.resolvePresentation(tag || {});
      const base = `${tag?.id || tag?.title || "tag"}-${presentation}-${key}`;
      const seed = this.colorSeed(base);
      const corporatePalette = [
        "hsl(201, 73%, 42%)", // azul corporativo
        "hsl(196, 64%, 46%)", // teal
        "hsl(168, 46%, 43%)", // verde azulado
        "hsl(128, 42%, 49%)", // verde medio
        "hsl(98, 40%, 51%)",  // verde oliva suave
        "hsl(39, 78%, 48%)",  // ambar
        "hsl(30, 72%, 50%)",  // naranja corporativo
        "hsl(16, 64%, 52%)",  // naranja rojizo suave
        "hsl(351, 55%, 53%)", // rojo controlado
        "hsl(277, 36%, 51%)", // violeta sobrio
        "hsl(224, 48%, 52%)", // azul secundario
        "hsl(212, 57%, 47%)", // azul petróleo
        "hsl(186, 49%, 44%)", // cian sobrio
        "hsl(47, 70%, 46%)",  // mostaza
      ];
      const baseColor = corporatePalette[seed % corporatePalette.length];
      return baseColor.replace("hsl(", "hsla(").replace(")", `, ${alpha})`);
    },
    periodTotals(tag) {
      const rows = this.getRowsByInfoKey(this.resolveInfoKey(tag));
      const totals = {};
      rows.forEach((row) => {
        const period = String(row?.fecha_cierre || "");
        if (!period) return;
        totals[period] = (totals[period] || 0) + (Number(row?.total_registros) || 0);
      });
      return totals;
    },
    currentPeriod(tag) {
      const totals = this.periodTotals(tag);
      const fromFilters = this.comparativoFechas.find((period) => Object.prototype.hasOwnProperty.call(totals, period));
      if (fromFilters) return fromFilters;
      return Object.keys(totals).sort().reverse()[0] || "";
    },
    calculatePercent(value, total) {
      if (!total) return 0;
      return Math.max(Math.min(Math.round((value / total) * 100), 100), 0);
    },
    metricDetails(tag) {
      const totals = this.periodTotals(tag);
      const currentPeriod = this.currentPeriod(tag);
      const value = Number(totals[currentPeriod] || 0);
      const total = Object.values(totals).reduce((acc, item) => acc + (Number(item) || 0), 0);
      return {
        value,
        total,
        percentage: this.calculatePercent(value, total),
      };
    },
    pieComparisons(tag) {
      const infoKey = this.resolveInfoKey(tag);
      const groups = this.buildComparativoGroups(infoKey, 5);
      if (!groups.length) return [];
      const periods = groups[0]?.periods || this.comparativoFechas;

      return periods.map((period) => {
        const slices = groups.map((group) => {
          const match = group.bars.find((bar) => bar.period === period);
          const value = Number(match?.value || 0);
          return {
            key: `${group.key}_${period}`,
            label: group.shortLabel,
            value,
            color: this.randomColor(tag, `pie_${group.key}`, 0.98),
          };
        });
        const total = slices.reduce((acc, slice) => acc + slice.value, 0);
        const normalized = total > 0
          ? slices.map((slice) => ({ ...slice, percent: this.calculatePercent(slice.value, total) }))
          : slices.map((slice) => ({ ...slice, percent: 0 }));
        return {
          period,
          total,
          slices: normalized,
          gradient: this.pieGradientFromSlices(normalized, total),
        };
      });
    },
    pieGradientFromSlices(slices, total) {
      if (!total) {
        return "conic-gradient(rgba(255,255,255,0.25) 0% 100%)";
      }
      let cursor = 0;
      const steps = slices.map((slice) => {
        const portion = (slice.value / total) * 100;
        const from = cursor;
        const to = cursor + portion;
        cursor = to;
        return `${slice.color} ${from}% ${to}%`;
      });
      return `conic-gradient(${steps.join(", ")})`;
    },
    pieLegend(tag) {
      const comparisons = this.pieComparisons(tag);
      if (!comparisons.length) return [];
      const current = comparisons[0];
      return current.slices
        .slice(0, 5)
        .map((slice) => ({
          key: slice.key,
          label: slice.label,
          color: slice.color,
          percent: slice.percent,
        }));
    },
    progressPercent(tag) {
      const details = this.metricDetails(tag);
      return details.percentage;
    },
    progressFillStyle(tag) {
      const start = this.randomColor(tag, "progress-start", 0.95);
      const end = this.randomColor(tag, "progress-end", 0.95);
      return {
        width: `${this.progressPercent(tag)}%`,
        background: `linear-gradient(90deg, ${start} 0%, ${end} 100%)`,
      };
    },
    lineStrokeStyle(tag) {
      return {
        stroke: this.randomColor(tag, "line", 0.95),
      };
    },
    areaFillStyle(tag) {
      return {
        fill: this.randomColor(tag, "area", 0.42),
      };
    },
    barItems(tag) {
      const infoKey = this.resolveInfoKey(tag);
      let series = [];

      if (infoKey === "comparativo_tasas_anual_tercero") {
        const rows = this.getRowsByInfoKey(infoKey);
        series = rows.slice(0, 20).map((row, index) => {
          const tercero = this.dimensionValueFromRow(row, infoKey);
          const periodo = String(row?.fecha_cierre || "");
          const yearShort = periodo.slice(2, 4);
          const terceroShort = tercero.split(" ").slice(0, 1).join(" ").slice(0, 9);
          return {
            key: `${row.identificacion || tercero}_${periodo}_${index}`,
            label: `${tercero} ${periodo}`.trim(),
            shortLabel: `${terceroShort} ${yearShort}`.trim(),
            value: Number(row.total_registros) || 0,
          };
        });
      } else {
        series = this.seriesWithLabels(tag).map((item) => ({
          ...item,
          shortLabel: String(item.label || "")
            .split(" ")
            .slice(0, 2)
            .join(" ")
            .slice(0, 12),
        }));
      }

      const maxValue = Math.max(...series.map((item) => item.value), 1);
      const total = series.reduce((acc, item) => acc + item.value, 0);
      return series.map((item, index) => ({
        key: item.key,
        label: item.label,
        shortLabel: item.shortLabel,
        value: item.value,
        percentage: this.calculatePercent(item.value, total),
        selected: index === 0,
        color: this.randomColor(tag, item.key, 0.95),
        height: Math.max(Math.round((item.value / maxValue) * 100), item.value > 0 ? 18 : 8),
      }));
    },
    groupedBarItems(tag) {
      const infoKey = this.resolveInfoKey(tag);
      const groups = this.buildComparativoGroups(infoKey, 5);

      if (groups.length) {
        const maxValue = Math.max(
          1,
          ...groups.flatMap((group) => group.bars.map((bar) => Number(bar.value) || 0))
        );

        return groups.map((group) => ({
          key: group.key,
          shortLabel: group.shortLabel,
          tooltip: `${group.label} (${group.periods.join(" vs ")})`,
          bars: group.bars.map((bar) => ({
            ...bar,
            color: this.randomColor(tag, `period_${bar.period}`, 0.95),
            height: Math.max(Math.round(((Number(bar.value) || 0) / maxValue) * 100), Number(bar.value) > 0 ? 16 : 6),
          })),
        }));
      }

      return this.barItems(tag).map((item, index) => ({
        key: `${item.key}_${index}`,
        shortLabel: item.shortLabel,
        tooltip: `${item.label}: ${item.value} (${item.percentage}%)`,
        bars: [
          {
            period: "single",
            periodShort: "",
            value: item.value,
            avgRate: "",
            color: item.color,
            height: item.height,
          },
        ],
      }));
    },
    horizontalBarItems(tag) {
      const infoKey = this.resolveInfoKey(tag);
      const isComparativo = ["comparativo_tasas_anual_tercero", "comparativo_tasas_anual_tipo_activo"].includes(infoKey);
      const base = this.seriesWithLabels(tag).slice(0, isComparativo ? 10 : 4);
      const maxValue = Math.max(...base.map((item) => item.value), 1);
      const total = base.reduce((acc, item) => acc + item.value, 0);
      return base.map((item, index) => ({
        key: item.key,
        label: item.label.slice(0, 12),
        value: item.value,
        avgRate: item.avgRate || "",
        percentage: this.calculatePercent(item.value, total),
        selected: index === 0,
        color: this.randomColor(tag, item.key, 0.95),
        width: Math.max(Math.round((item.value / maxValue) * 100), 8),
      }));
    },
    seriesWithLabels(tag) {
      const infoKey = this.resolveInfoKey(tag);
      const groups = this.buildComparativoGroups(infoKey, 5);
      if (groups.length) {
        return groups.flatMap((group) =>
          group.bars.map((bar, index) => ({
            key: `${group.key}_${bar.period}_${index}`,
            label: `${group.shortLabel} ${bar.periodShort}`.trim(),
            value: bar.value,
            avgRate: bar.avgRate,
          }))
        );
      }

      const rows = this.getRowsByInfoKey(infoKey);
      const grouped = {};
      rows.forEach((row) => {
        const dimensionValue = this.dimensionValueFromRow(row, infoKey);
        grouped[dimensionValue] = (grouped[dimensionValue] || 0) + (Number(row.total_registros) || 0);
      });
      return Object.entries(grouped)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 6)
        .map(([label, value], index) => ({
          key: `${infoKey}_${index}_${label}`,
          label,
          value,
          avgRate: "",
        }));
    },
    pointTitle(point) {
      const ratePart = point?.avgRate ? ` | ${point.avgRate}` : "";
      return `${point.label}: ${point.value} (${point.percentage}%)${ratePart}`;
    },
    linePoints(tag) {
      const base = this.seriesWithLabels(tag);
      const maxValue = Math.max(...base.map((item) => item.value), 1);
      const total = base.reduce((acc, item) => acc + item.value, 0);
      return base.slice(0, 6).map((item, index, all) => ({
        key: item.key,
        label: item.label,
        value: item.value,
        avgRate: item.avgRate || "",
        percentage: this.calculatePercent(item.value, total),
        selected: index === 0,
        x: all.length === 1 ? 50 : Math.round((index / (all.length - 1)) * 100),
        y: 100 - Math.round((item.value / maxValue) * 88),
      }));
    },
    linePolyline(tag) {
      return this.linePoints(tag).map((point) => `${point.x},${point.y}`).join(" ");
    },
    areaPolygon(tag) {
      const points = this.linePoints(tag);
      if (!points.length) return "0,100 100,100";
      const line = points.map((point) => `${point.x},${point.y}`).join(" ");
      return `${points[0].x},100 ${line} ${points[points.length - 1].x},100`;
    },
    gaugeStyle(tag) {
      const pct = this.progressPercent(tag);
      return {
        background: `conic-gradient(from 180deg, #7ec8e3 0% ${pct}%, rgba(255,255,255,0.22) ${pct}% 100%)`,
      };
    },
    rankingItems(tag) {
      const base = this.seriesWithLabels(tag);
      const total = base.reduce((acc, item) => acc + item.value, 0);
      return base
        .sort((a, b) => b.value - a.value)
        .slice(0, 4)
        .map((item, index) => ({
          ...item,
          selected: index === 0,
          label: item.label.slice(0, 14),
          avgRate: item.avgRate || "",
          percentage: this.calculatePercent(item.value, total),
        }));
    },
    tableItems(tag) {
      const infoKey = this.resolveInfoKey(tag);
      const rows = this.getRowsByInfoKey(infoKey);
      return rows.map((row, index) => ({
        key: `${this.dimensionValueFromRow(row, infoKey)}_${row.fecha_cierre}_${index}`,
        dimensionValue: this.dimensionValueFromRow(row, infoKey),
        fechaCierre: row.fecha_cierre,
        tasaPromedio: Number(row.tasa || 0).toFixed(4),
        totalRegistros: Number(row.total_registros || 0),
      }));
    },
    tagStyle(tag) {
      return {
        left: `${tag.left}px`,
        top: `${tag.top}px`,
        width: `${tag.width}px`,
        height: `${tag.height}px`,
        backgroundColor: tag.color,
        color: tag.textColor,
        zIndex: tag.zIndex,
      };
    },
    toRect(tag, override = {}) {
      return {
        left: Number(override.left ?? tag.left) || 0,
        top: Number(override.top ?? tag.top) || 0,
        width: Number(override.width ?? tag.width) || 0,
        height: Number(override.height ?? tag.height) || 0,
      };
    },
    rectsOverlap(a, b) {
      return (
        a.left < b.left + b.width &&
        a.left + a.width > b.left &&
        a.top < b.top + b.height &&
        a.top + a.height > b.top
      );
    },
    hasCollision(candidateRect, ignoreId = null) {
      return this.tags.some((tag) => {
        if (ignoreId !== null && tag.id === ignoreId) return false;
        return this.rectsOverlap(candidateRect, this.toRect(tag));
      });
    },
    getCanvasSize() {
      const fallbackWidth = 1200;
      const fallbackHeight = 780;
      if (!this.$refs.canvas) {
        return { width: fallbackWidth, height: fallbackHeight };
      }
      return {
        width: this.$refs.canvas.clientWidth || fallbackWidth,
        height: this.$refs.canvas.clientHeight || fallbackHeight,
      };
    },
    findAvailablePosition(width, height) {
      const { width: canvasWidth, height: canvasHeight } = this.getCanvasSize();
      const maxLeft = Math.max(canvasWidth - width, 0);
      const maxTop = Math.max(canvasHeight - height, 0);
      const step = 20;

      for (let top = 12; top <= maxTop; top += step) {
        for (let left = 12; left <= maxLeft; left += step) {
          const candidate = { left, top, width, height };
          if (!this.hasCollision(candidate)) {
            return { left, top };
          }
        }
      }

      return null;
    },
    notifyNoSpace() {
      if (this.$toast?.warning) {
        this.$toast.warning(this.labels.noSpace);
      }
    },
    async createTag() {
      if (!this.ensureCanManageTags()) return;
      if (this.isLimitReached) {
        this.notifyLimitReached();
        return;
      }

      const minSize = this.getTagMinSize(this.draft);
      const defaultWidth = Math.max(220, minSize.minWidth);
      const defaultHeight = Math.max(140, minSize.minHeight);
      const freePosition = this.findAvailablePosition(defaultWidth, defaultHeight);
      if (!freePosition) {
        this.notifyNoSpace();
        return;
      }

      const tag = {
        id: null,
        title: this.draft.title?.trim() || this.metricLabel(this.draft),
        infoKey: this.draft.infoKey,
        presentationType: this.draft.presentationType,
        width: defaultWidth,
        height: defaultHeight,
        left: freePosition.left,
        top: freePosition.top,
        color: this.draft.color || "#f5faff",
        textColor: this.draft.textColor || "#000000",
        zIndex: ++this.zSeed,
      };

      try {
        const res = await DashboardEtiquetas.create(this.buildEtiquetaPayload(tag));
        tag.id = res?.data?.id;
        this.tags.push(tag);
        this.activeTagId = tag.id;
        this.resetDraft();
        this.showCreator = false;
      } catch (error) {
        this.$toast?.error?.("No se pudo crear la etiqueta en base de datos.");
      }
    },
    resetDraft() {
      this.draft = {
        title: "",
        infoKey: "comparativo_tasas_anual_tipo_activo",
        presentationType: "table",
        color: "#f5faff",
        textColor: "#000000",
      };
    },
    async deleteTag(tagId) {
      if (!this.ensureCanManageTags()) return;
      const confirmed = window.confirm(this.labels.confirmDeleteTag);
      if (!confirmed) return;
      try {
        await DashboardEtiquetas.delete(tagId);
        this.tags = this.tags.filter((tag) => tag.id !== tagId);
        if (this.activeTagId === tagId) this.activeTagId = null;
        if (this.searchTagId === tagId) this.searchTagId = null;
      } catch (error) {
        this.$toast?.error?.("No se pudo eliminar la etiqueta en base de datos.");
      }
    },
    startDrag(event, tagId) {
      if (!this.ensureCanManageTags(false)) return;
      const tag = this.tags.find((item) => item.id === tagId);
      if (!tag) return;
      this.raiseTag(tag);
      this.activeTagId = tagId;
      this.interaction = {
        type: "drag",
        tagId,
        startX: event.clientX,
        startY: event.clientY,
        left: tag.left,
        top: tag.top,
      };
    },
    startResize(event, tagId) {
      if (!this.ensureCanManageTags(false)) return;
      const tag = this.tags.find((item) => item.id === tagId);
      if (!tag) return;
      this.raiseTag(tag);
      this.activeTagId = tagId;
      this.interaction = {
        type: "resize",
        tagId,
        startX: event.clientX,
        startY: event.clientY,
        width: tag.width,
        height: tag.height,
      };
    },
    onPointerMove(event) {
      if (!this.interaction) return;
      const tag = this.tags.find((item) => item.id === this.interaction.tagId);
      if (!tag || !this.$refs.canvas) return;

      const canvasRect = this.$refs.canvas.getBoundingClientRect();
      const deltaX = event.clientX - this.interaction.startX;
      const deltaY = event.clientY - this.interaction.startY;

      if (this.interaction.type === "drag") {
        const maxLeft = Math.max(canvasRect.width - tag.width, 0);
        const maxTop = Math.max(canvasRect.height - tag.height, 0);
        const nextLeft = this.clamp(this.interaction.left + deltaX, 0, maxLeft);
        const nextTop = this.clamp(this.interaction.top + deltaY, 0, maxTop);
        const candidate = this.toRect(tag, { left: nextLeft, top: nextTop });

        if (!this.hasCollision(candidate, tag.id)) {
          tag.left = nextLeft;
          tag.top = nextTop;
        }
      } else if (this.interaction.type === "resize") {
        const minSize = this.getTagMinSize(tag);
        const maxWidth = Math.max(canvasRect.width - tag.left, minSize.minWidth);
        const maxHeight = Math.max(canvasRect.height - tag.top, minSize.minHeight);
        const nextWidth = this.clamp(this.interaction.width + deltaX, minSize.minWidth, maxWidth);
        const nextHeight = this.clamp(this.interaction.height + deltaY, minSize.minHeight, maxHeight);
        const candidate = this.toRect(tag, { width: nextWidth, height: nextHeight });

        if (!this.hasCollision(candidate, tag.id)) {
          tag.width = nextWidth;
          tag.height = nextHeight;
        }
      }
    },
    async onPointerUp() {
      if (!this.interaction) return;
      const tagId = this.interaction.tagId;
      this.interaction = null;
      const tag = this.tags.find((item) => item.id === tagId);
      if (tag) await this.persistTag(tag);
    },
    raiseTag(tag) {
      tag.zIndex = ++this.zSeed;
    },
    clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    },
    async loadTags() {
      try {
        const res = await DashboardEtiquetas.getAll();
        const parsed = Array.isArray(res?.data?.listDashboardEtiquetas) ? res.data.listDashboardEtiquetas : [];
        if (!Array.isArray(parsed)) return;
        this.tags = parsed
          .slice(0, this.maxTags)
          .map((rawTag, index) => {
            const normalizedTag = {
              id: rawTag.id || Date.now() + index,
              title: rawTag.titulo || this.labels.fallbackTagTitle,
              infoKey: rawTag.info_key || null,
              presentationType: rawTag.presentation_type || null,
              infoType: rawTag.infoType || null,
              left: Math.max(Number(rawTag.pos_x) || 0, 0),
              top: Math.max(Number(rawTag.pos_y) || 0, 0),
              color: rawTag.color_fondo || "#f5faff",
              textColor: rawTag.color_texto || "#000000",
              zIndex: Number(rawTag.z_index) || index + 1,
            };
            normalizedTag.infoKey = this.resolveInfoKey(normalizedTag);
            normalizedTag.presentationType = this.resolvePresentation(normalizedTag);
            const minSize = this.getTagMinSize(normalizedTag);

            return {
              ...normalizedTag,
              width: this.clamp(Number(rawTag.width_px) || 220, minSize.minWidth, 480),
              height: this.clamp(Number(rawTag.height_px) || 140, minSize.minHeight, 320),
            };
          })
          .map((tag) => ({
            ...tag,
            infoKey: this.resolveInfoKey(tag),
            presentationType: this.resolvePresentation(tag),
          }));

        this.tags.forEach((tag) => {
          const rect = this.toRect(tag);
          if (this.hasCollision(rect, tag.id)) {
            const freePosition = this.findAvailablePosition(tag.width, tag.height);
            if (freePosition) {
              tag.left = freePosition.left;
              tag.top = freePosition.top;
            }
          }
        });

        this.zSeed = this.tags.reduce((max, tag) => Math.max(max, tag.zIndex), 1);
      } catch (error) {
        this.tags = [];
      }
    },
    getPeriodosFallback() {
      const now = new Date();
      const lastClosed = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const prevYearSameMonth = new Date(lastClosed.getFullYear() - 1, lastClosed.getMonth(), 1);
      const toPeriod = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      return [toPeriod(lastClosed), toPeriod(prevYearSameMonth)];
    },
    getDefaultComparedPeriod(periodoActual, periodos) {
      const base = String(periodoActual || "");
      if (!base || !Array.isArray(periodos) || !periodos.length) return "";
      const [year, month] = base.split("-");
      const periodoMismoMesAnioAnterior = `${Number(year) - 1}-${month}`;
      if (periodos.includes(periodoMismoMesAnioAnterior)) return periodoMismoMesAnioAnterior;
      const alterno = periodos.find((item) => item !== base);
      return alterno || base;
    },
    async loadPeriodosDisponibles() {
      try {
        const res = await DashboardMetricas.getFechasComparativoTasas();
        const fromApi = Array.isArray(res?.data?.fechasDisponibles) ? res.data.fechasDisponibles : [];
        this.periodosDisponibles = fromApi.length ? fromApi : this.getPeriodosFallback();
      } catch (error) {
        this.periodosDisponibles = this.getPeriodosFallback();
      }

      if (!this.selectedPeriodoActual || !this.periodosDisponibles.includes(this.selectedPeriodoActual)) {
        this.selectedPeriodoActual = this.periodosDisponibles[0] || "";
      }
      if (!this.selectedPeriodoComparado || !this.periodosDisponibles.includes(this.selectedPeriodoComparado)) {
        this.selectedPeriodoComparado = this.getDefaultComparedPeriod(this.selectedPeriodoActual, this.periodosDisponibles);
      }
    },
    async applyPeriodoFilter() {
      await this.loadMetricData();
    },
    async loadMetricData() {
      this.isLoadingMetricas = true;
      try {
        let periodos = [this.selectedPeriodoActual, this.selectedPeriodoComparado].filter(Boolean);
        if (!periodos.length) {
          periodos = this.getPeriodosFallback();
        }
        const [resActivo, resContrato, resTercero] = await Promise.allSettled([
          DashboardMetricas.getComparativoTasasAnualPorTipoActivo(periodos),
          DashboardMetricas.getComparativoTasasAnualPorTipoContrato(periodos),
          DashboardMetricas.getComparativoTasasAnualPorTercero(periodos, {
            incluirTodos: this.incluirTodosTerceros,
            tercero: this.terceroFilter,
          }),
        ]);
        const activoData = resActivo.status === "fulfilled" ? resActivo.value?.data : null;
        const contratoData = resContrato.status === "fulfilled" ? resContrato.value?.data : null;
        const terceroData = resTercero.status === "fulfilled" ? resTercero.value?.data : null;
        const fechasActivo = Array.isArray(activoData?.fechas) ? activoData.fechas : [];
        const fechasContrato = Array.isArray(contratoData?.fechas) ? contratoData.fechas : [];
        const fechasTercero = Array.isArray(terceroData?.fechas) ? terceroData.fechas : [];
        this.comparativoFechas = fechasActivo.length
          ? fechasActivo
          : (fechasContrato.length ? fechasContrato : (fechasTercero.length ? fechasTercero : periodos));
        this.comparativoTasasRows = Array.isArray(activoData?.comparativo) ? activoData.comparativo : [];
        this.comparativoTasasTipoContratoRows = Array.isArray(contratoData?.comparativo) ? contratoData.comparativo : [];
        this.comparativoTasasTerceroRows = Array.isArray(terceroData?.comparativo) ? terceroData.comparativo : [];
      } catch (error) {
        this.comparativoFechas = [];
        this.comparativoTasasRows = [];
        this.comparativoTasasTipoContratoRows = [];
        this.comparativoTasasTerceroRows = [];
      } finally {
        this.isLoadingMetricas = false;
      }
    },
    async loadContextData() {
      try {
        await Menu.getAllMenu();
      } catch (error) {
        // Sin acción: no bloquea métricas del dashboard.
      }
      await this.loadPeriodosDisponibles();
      await this.loadMetricData();
      this.canManageTags = String(this.$store.state.userRole) === "1";

      if (!this.canManageTags) {
        this.showCreator = false;
        this.cancelTagEdit();
      }
    },
  },
  mounted() {
    this.loadTags();
    this.canManageTags = String(this.$store.state.userRole) === "1";
    this.loadContextData();
    window.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("pointerup", this.onPointerUp);
  },
  beforeUnmount() {
    if (this.terceroFilterDebounce) {
      clearTimeout(this.terceroFilterDebounce);
      this.terceroFilterDebounce = null;
    }
    window.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);
  },
};
</script>

<style src="../main.css"></style>
